<template>
  <div class="body-set-password" v-if="expiration">
    <div v-if="showForm">
      <b-row class="justify-content-md-center">
        <b-col cols="12" class="text-center">
          <img
            :src="logoUrl"
            alt="sbo-logo"
            style="height: 100px"
            class="my-3"
          />
        </b-col>
      </b-row>
      <b-row class="justify-content-md-center">
        <b-col cols="12" lg="4" md="8">
          <b-card class="m-2" no-body>
            <div class="m-2">
              <div class="mb-2 mt-1 text-center">
                <h2>
                  {{
                    $t("messages.greetings.change_password", {
                      name: `${user.name} ${user.last_name}`,
                    })
                  }}
                </h2>
                <br />
                <span>
                  {{ $t("users.set_password.password_rules") }}
                </span>
              </div>
              <div style="margin: auto">
                <div>
                  <b-form-group
                    :label="$t('users.set_password.password')"
                    label-for="password"
                    class="text-capitalize"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="password"
                      vid="password"
                      rules="required|min:8|password"
                    >
                      <b-form-input
                        id="password"
                        type="password"
                        class="d-inline-block mr-1 text-capitalize"
                        :placeholder="$t('users.set_password.password')"
                        v-model="newPassword"
                      />
                      <small class="text-danger">
                        {{ errors[0] }}
                      </small>
                    </validation-provider>
                  </b-form-group>
                </div>
                <div class="mt-2 mb-3">
                  <b-form-group
                    :label="$t('users.set_password.password_retype')"
                    label-for="password_retype"
                    class="text-capitalize"
                  >
                    <validation-provider
                      #default="{ errors }"
                      :name="$t('users.set_password.password_retype')"
                      vid="password_retype"
                      rules="required|confirmed:password|min:8|password"
                    >
                      <b-form-input
                        id="password_retype"
                        class="d-inline-block mr-1 text-capitalize"
                        type="password"
                        :placeholder="$t('users.set_password.password_retype')"
                        v-model="retypePassword"
                      />
                      <small class="text-danger">
                        {{ errors[0] }}
                      </small>
                    </validation-provider>
                  </b-form-group>
                </div>
                <div class="mt-3">
                  <b-button
                    variant="secondary"
                    style="float: right"
                    :disabled="validatePassword || disabledButton"
                    @click="changePassword()"
                  >
                    <span class="text-nowrap text-capitalize">{{
                      $t("actions.save")
                    }}</span>
                    <b-spinner small class="ml-1" v-if="disabledButton"></b-spinner>
                  </b-button>
                </div>
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </div>
    <div
      v-else
      class="fullscreen-container d-flex justify-content-center align-items-center"
    >
      <b-spinner variant="light"></b-spinner>
    </div>
  </div>
  <div
    v-else
    class="fullscreen-container d-flex justify-content-center align-items-center"
  >
    <div
      v-if="!expiration"
      class="d-flex justify-content-center flex-column align-items-center"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        xmlns:xlink="http://www.w3.org/1999/xlink"
        viewBox="0 0 24 24"
        width="100"
        height="100"
        class="mb-2"
      >
        <path
          d="M17 7h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1c0 1.43-.98 2.63-2.31 2.98l1.46 1.46C20.88 15.61 22 13.95 22 12c0-2.76-2.24-5-5-5zm-1 4h-2.19l2 2H16zM2 4.27l3.11 3.11A4.991 4.991 0 0 0 2 12c0 2.76 2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1c0-1.59 1.21-2.9 2.76-3.07L8.73 11H8v2h2.73L13 15.27V17h1.73l4.01 4L20 19.74L3.27 3L2 4.27z"
          fill="red"
        ></path>
      </svg>
      <div class="text-white display-4 margin-auto text-capitalize-first">
        {{ $t("users.expired_link") }}
      </div>
    </div>
  </div>
</template>

<script>
import { BCard, BRow, BCol, BFormInput, BButton, BImg } from "bootstrap-vue";
import vSelect from "vue-select";
import { ref, onMounted, computed } from "@vue/composition-api";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required, confirmed, min, password } from "@validations";
import axios from "axios";
import router from "@/router";
import { useUtils as useI18nUtils } from "@core/libs/i18n";

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BImg,
    vSelect,
    ValidationProvider,
    ValidationObserver,
  },
  setup(_, context) {
    const showForm = ref(false);
    const newPassword = ref("");
    const retypePassword = ref("");
    const disabledButton = ref(false);
    const toast = useToast();
    const user = ref({});
    const expiration = ref(true);
    const { t } = useI18nUtils();
    const trans = {
      "messages.redirect.login": t("messages.redirect.login"),
    };
    const token = router.currentRoute.params.data;
    const url = `${window.__env.VUE_APP_BASE_URL}/v1/`;

    onMounted(() => {
      validateLink();
    });

    const validateLink = () => {
      axios
        .get(url+'user/reset-password/validate-link', {
          headers: {
            Authorization: `Bearer ${token}`,
          }
        })
        .then((response) => {
          if(response.data.success){
            getUser()
          }
          else{
            showForm.value = false;
            expiration.value = false;
          }
        })
        .catch((response) => {
          expiration.value = false;
        });
    };

    const getUser = async () => {
      axios
        .get(url+'users/data', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then(({ data: resp }) => {
          user.value = resp.data;
          showForm.value = true;
        })
        .catch((response) => {
          if (response.response.status == 401) {
            router.push({ name: "auth-login" });
          }
        });
    }

    const validatePassword = computed(() => {
      return (newPassword.value === '' || retypePassword.value === '' || newPassword.value !== retypePassword.value )
    });

    const changePassword = () => {
      if (newPassword.value !== retypePassword.value) {
        return;
      }

      disabledButton.value = true;
      const req = {
        password: newPassword.value,
        password_confirmation: retypePassword.value,
      };

      const urlPassword = `${window.__env.VUE_APP_BASE_URL}/v1/user/setPassword`;

      axios
        .put(urlPassword, req, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then(({ data: resp }) => {
          toast({
            component: ToastificationContent,
            props: {
              title: resp.message,
              text: trans["messages.redirect.login"],
              icon: "CheckIcon",
              variant: "success",
            },
          });

          setTimeout(() => {
            router.push({ name: "auth-login" });
          }, 5000);
        });
    };

    return {
      validatePassword,
      newPassword,
      retypePassword,
      logoUrl: require("@/assets/images/sbo_logo.png"),
      changePassword,
      user,
      disabledButton,
      expiration,
      showForm,
    };
  },
};
</script>

<style scoped>
.fullscreen-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #1b3d5e;
}

.body-set-password {
  background-color: #1b3d5e !important;
  height: 100vh;
}

.card-margin {
  transform: translate(-50%, 50%);
  left: 50%;
  margin: auto !important;
}
</style>